import {type AppBskyActorDefs, type AppBskyGraphDefs} from '@atproto/api'
import {TID} from '@atproto/common-web'
import {useQueryClient} from '@tanstack/react-query'
import {useEventCallback} from 'usehooks-ts'

import {BSKY_APP_ACCOUNT_DID, VIDEO_SAVED_FEED} from '#/lib/constants'
import {logEvent, useGate} from '#/lib/statsig/statsig'
import {logger} from '#/logger'
import {useSetHasCheckedForStarterPack} from '#/state/preferences/used-starter-packs'
import {getAllListMembers} from '#/state/queries/list-members.ts'
import {preferencesQueryKey} from '#/state/queries/preferences'
import {RQKEY as profileRQKey} from '#/state/queries/profile'
import {useAgent} from '#/state/session'
import {
  useActiveStarterPack,
  useSetActiveStarterPack,
} from '#/state/shell/starter-pack.tsx'
import {bulkWriteFollows} from '#/screens/Onboarding/util'

export function useFinishOnboarding() {
  const agent = useAgent()
  const gate = useGate()
  const queryClient = useQueryClient()
  const activeStarterPack = useActiveStarterPack()
  const setActiveStarterPack = useSetActiveStarterPack()
  const setHasCheckedForStarterPack = useSetHasCheckedForStarterPack()

  return useEventCallback(async () => {
    let starterPack: AppBskyGraphDefs.StarterPackView | undefined
    let listItems: AppBskyGraphDefs.ListItemView[] | undefined

    if (activeStarterPack?.uri) {
      try {
        const spRes = await agent.app.bsky.graph.getStarterPack({
          starterPack: activeStarterPack.uri,
        })
        starterPack = spRes.data.starterPack
      } catch (e) {
        logger.error('Failed to fetch starter pack', {safeMessage: e})
      }
      try {
        if (starterPack?.list) {
          listItems = await getAllListMembers(agent, starterPack.list.uri)
        }
      } catch (e) {
        logger.error('Failed to fetch starter pack list items', {
          safeMessage: e,
        })
      }
    }

    try {
      const followDids: string[] = [
        BSKY_APP_ACCOUNT_DID,
        ...(listItems?.map(i => i.subject.did) ?? []),
      ]
      const starterPackFeeds: AppBskyActorDefs.SavedFeed[] = []

      if (gate('onboarding_add_video_feed')) {
        starterPackFeeds.push({...VIDEO_SAVED_FEED, id: TID.nextStr()})
      }
      if (starterPack?.feeds?.length) {
        starterPackFeeds.push(
          ...starterPack.feeds.map(f => ({
            type: 'feed',
            value: f.uri,
            pinned: true,
            id: TID.nextStr(),
          })),
        )
      }

      await Promise.all([
        bulkWriteFollows(agent, followDids),
        (async () => {
          if (starterPackFeeds.length === 0) return
          // Base feeds (discover, timeline, list) were already added in
          // createAgentAndCreateAccount. Only add starter-pack/video extras here.
          const existingPrefs = await agent.getPreferences()
          const existingValues = new Set(
            (existingPrefs.savedFeeds ?? []).map(f => f.value),
          )
          const toAdd = starterPackFeeds.filter(
            f => !existingValues.has(f.value),
          )
          if (toAdd.length > 0) {
            await agent.addSavedFeeds(toAdd)
          }
        })(),
      ])
    } catch (e: any) {
      logger.info('onboarding: bulk save failed')
      logger.error(e)
    }

    await Promise.all([
      queryClient.invalidateQueries({queryKey: preferencesQueryKey}),
      queryClient.invalidateQueries({
        queryKey: profileRQKey(agent.session?.did ?? ''),
      }),
    ]).catch(e => {
      logger.error(e)
    })

    setActiveStarterPack(undefined)
    setHasCheckedForStarterPack(true)

    if (starterPack && listItems?.length) {
      logEvent('starterPack:followAll', {
        logContext: 'Onboarding',
        starterPack: starterPack.uri,
        count: listItems.length,
      })
    }
  })
}
